// src/hooks/useJobExecutions.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems, postItem } from '../support/queries';
import { JobExecution, TaskExecution } from '../types/taskManager';

interface UseJobExecutionsOptions {
  status?: string;
  job_type?: string;
  start_date?: string;
  end_date?: string;
}

export const useJobExecutions = (filters?: UseJobExecutionsOptions) => {
  const queryClient = useQueryClient();

  // Build query params
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.job_type) params.append('job_type', filters.job_type);
  if (filters?.start_date) params.append('start_date', filters.start_date);
  if (filters?.end_date) params.append('end_date', filters.end_date);

  const endpoint = `queue/job-executions/${params.toString() ? '?' + params.toString() : ''}`;

  // Fetch job executions
  const query = useQuery<JobExecution[]>({
    queryKey: ['job-executions', filters],
    queryFn: () => fetchItems(endpoint),
    ...{
        refetchInterval: 30000, // Changed from 10s to 30s
        staleTime: 20000,       // Changed from 5s to 20s
      },
  });

  // Retry job
  const retryMutation = useMutation({
    mutationFn: (jobId: string) => postItem(`queue/job-executions/${jobId}/retry/`, {}),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['job-executions'] });
      queryClient.invalidateQueries({ queryKey: ['queue-status'] });
      return data;
    },
    onError: (error: any) => {
      console.error('Failed to retry job:', error);
    },
  });

  // Cancel stuck jobs
  const cancelStuckMutation = useMutation({
    mutationFn: (params: { hours?: number }) => postItem('queue/cancel-stuck/', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-executions'] });
    },
  });

  return {
    jobs: query.data || [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    retryJob: retryMutation.mutateAsync,
    cancelStuck: cancelStuckMutation.mutateAsync,
    isRetrying: retryMutation.isPending,
    isCancelling: cancelStuckMutation.isPending,
    retryError: retryMutation.error,
    cancelError: cancelStuckMutation.error,
  };
};

// Job Details Hook
export const useJobDetails = (jobId: string | null) => {
  const query = useQuery<JobExecution>({
    queryKey: ['job-details', jobId],
    queryFn: () => fetchItems(`queue/job-executions/${jobId}/`),
    ...{
        enabled: !!jobId,
        refetchInterval: 5000,
      },
  });

  return {
    job: query.data,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};